/**
 * DisplayManager.js - Manage display devices (Android screens)
 */

import React, { useState } from 'react';
import axiosInstance from '../../axiosInstance';

const DisplayManager = ({ displays, onRefresh }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDisplay, setSelectedDisplay] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    displayId: '',
    ipAddress: '',
    port: 5555,
    resolution: { width: 1920, height: 1080 },
    orientation: 'landscape',
    playerType: 'custom',
    location: '',
    notes: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axiosInstance.post('/video-sync/displays', formData);
      alert('Display added successfully!');
      setShowAddModal(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('[DisplayManager] Add display error:', error);
      alert('Failed to add display: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      await axiosInstance.put(`/video-sync/displays/${selectedDisplay._id}`, formData);
      alert('Display updated successfully!');
      setShowEditModal(false);
      setSelectedDisplay(null);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('[DisplayManager] Edit display error:', error);
      alert('Failed to update display: ' + (error.response?.data?.error || error.message));
    }
  };

  const openEditModal = (display) => {
    setSelectedDisplay(display);
    setFormData({
      name: display.name,
      displayId: display.displayId,
      ipAddress: display.ipAddress,
      port: display.port,
      resolution: display.resolution,
      orientation: display.orientation,
      playerType: display.playerType,
      location: display.location || '',
      notes: display.notes || '',
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayId: '',
      ipAddress: '',
      port: 5555,
      resolution: { width: 1920, height: 1080 },
      orientation: 'landscape',
      playerType: 'custom',
      location: '',
      notes: '',
    });
  };

  const checkConnectivity = async (displayId) => {
    try {
      const response = await axiosInstance.get(`/video-sync/displays/${displayId}/check`);
      const { connectivity } = response.data;

      alert(
        `Connectivity Check:\n\n` +
        `Ping: ${connectivity.ping ? '✅ Success' : '❌ Failed'}\n` +
        `ADB: ${connectivity.adb ? '✅ Connected' : '❌ Not Connected'}`
      );

      onRefresh();
    } catch (error) {
      console.error('[DisplayManager] Connectivity check error:', error);
      alert('Connectivity check failed');
    }
  };

  const deleteDisplay = async (displayId) => {
    if (!window.confirm('Are you sure you want to delete this display?')) return;

    try {
      await axiosInstance.delete(`/video-sync/displays/${displayId}`);
      alert('Display deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('[DisplayManager] Delete error:', error);
      alert('Delete failed');
    }
  };

  const getStatusIndicator = (status) => {
    const indicators = {
      online: { color: '#27ae60', icon: '🟢', text: 'Online' },
      offline: { color: '#95a5a6', icon: '⚪', text: 'Offline' },
      error: { color: '#e74c3c', icon: '🔴', text: 'Error' },
      syncing: { color: '#f39c12', icon: '🟡', text: 'Syncing' },
    };

    const indicator = indicators[status] || indicators.offline;

    return (
      <div className="status-indicator" style={{ color: indicator.color }}>
        <span className="status-icon">{indicator.icon}</span>
        <span className="status-text">{indicator.text}</span>
      </div>
    );
  };

  return (
    <div className="display-manager">
      <div className="manager-header">
        <h2>Display Management</h2>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          + Add Display
        </button>
      </div>

      {/* Display Grid */}
      <div className="display-grid">
        {displays.length === 0 ? (
          <div className="empty-state">
            <p>No displays registered yet. Add your first display above!</p>
          </div>
        ) : (
          displays.map((display) => (
            <div key={display._id} className="display-card">
              <div className="display-header">
                <h3>{display.name}</h3>
                {getStatusIndicator(display.status)}
              </div>

              <div className="display-info">
                <div className="info-row">
                  <strong>Display ID:</strong>
                  <span>{display.displayId}</span>
                </div>
                <div className="info-row">
                  <strong>IP Address:</strong>
                  <span>{display.ipAddress}:{display.port}</span>
                </div>
                <div className="info-row">
                  <strong>Resolution:</strong>
                  <span>{display.resolution.width}x{display.resolution.height}</span>
                </div>
                <div className="info-row">
                  <strong>Orientation:</strong>
                  <span>{display.orientation}</span>
                </div>
                {display.location && (
                  <div className="info-row">
                    <strong>Location:</strong>
                    <span>{display.location}</span>
                  </div>
                )}
                {display.lastSeen && (
                  <div className="info-row">
                    <strong>Last Seen:</strong>
                    <span>{new Date(display.lastSeen).toLocaleString()}</span>
                  </div>
                )}
                {display.currentVideo && (
                  <div className="info-row">
                    <strong>Current Video:</strong>
                    <span className="current-video-badge">
                      {display.currentVideo.name || 'Assigned'}
                    </span>
                  </div>
                )}
              </div>

              <div className="display-actions">
                <button
                  className="btn-secondary"
                  onClick={() => checkConnectivity(display._id)}
                >
                  🔍 Check Connection
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => openEditModal(display)}
                >
                  ✏️ Edit
                </button>
                <button
                  className="btn-danger"
                  onClick={() => deleteDisplay(display._id)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Display Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Display</h2>
            <button className="close-btn" onClick={() => setShowAddModal(false)}>✕</button>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Display Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Left Display"
                  required
                />
              </div>

              <div className="form-group">
                <label>Display ID * (unique identifier)</label>
                <input
                  type="text"
                  name="displayId"
                  value={formData.displayId}
                  onChange={handleInputChange}
                  placeholder="e.g., display-left-01"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>IP Address *</label>
                  <input
                    type="text"
                    name="ipAddress"
                    value={formData.ipAddress}
                    onChange={handleInputChange}
                    placeholder="e.g., 172.16.3.101"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>ADB Port</label>
                  <input
                    type="number"
                    name="port"
                    value={formData.port}
                    onChange={handleInputChange}
                    placeholder="5555"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Player Type</label>
                <select
                  name="playerType"
                  value={formData.playerType}
                  onChange={handleInputChange}
                >
                  <option value="custom">Custom Sync Player (Recommended)</option>
                  <option value="vlc">VLC Android</option>
                  <option value="mxplayer">MX Player</option>
                </select>
              </div>

              <div className="form-group">
                <label>Location (optional)</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., Lobby - Left Wall"
                />
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes about this display..."
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Display
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Display Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => { setShowEditModal(false); setSelectedDisplay(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Display</h2>
            <button className="close-btn" onClick={() => { setShowEditModal(false); setSelectedDisplay(null); }}>✕</button>

            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Display Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Left Display"
                  required
                />
              </div>

              <div className="form-group">
                <label>Display ID * (unique identifier)</label>
                <input
                  type="text"
                  name="displayId"
                  value={formData.displayId}
                  onChange={handleInputChange}
                  placeholder="e.g., display-left-01"
                  required
                  disabled
                  style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                  title="Display ID cannot be changed after creation"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>IP Address *</label>
                  <input
                    type="text"
                    name="ipAddress"
                    value={formData.ipAddress}
                    onChange={handleInputChange}
                    placeholder="e.g., 172.16.3.101"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>ADB Port</label>
                  <input
                    type="number"
                    name="port"
                    value={formData.port}
                    onChange={handleInputChange}
                    placeholder="5555"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Player Type</label>
                <select
                  name="playerType"
                  value={formData.playerType}
                  onChange={handleInputChange}
                >
                  <option value="custom">Custom Sync Player (Recommended)</option>
                  <option value="vlc">VLC Android</option>
                  <option value="mxplayer">MX Player</option>
                </select>
              </div>

              <div className="form-group">
                <label>Location (optional)</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g., Lobby - Left Wall"
                />
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes about this display..."
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowEditModal(false); setSelectedDisplay(null); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update Display
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayManager;
